import { getFetcher } from '@/api/fetchers';
import { MonthSelector } from '@/components/MonthSelector/MonthSelector';
import { SectionSelector } from '@/components/Section/SectionSelector';
import { type Services } from '@/schemas/API/GET/service/response';
import { type ComboboxItem, Stack, NativeSelect } from '@mantine/core';
import dayjs, { type Dayjs } from 'dayjs';
import {
  type ReactElement,
  useState,
  createContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import useSWR from 'swr';

export type PlanningState = 'past' | 'present' | 'future';

export interface PlanningContextType {
  state: PlanningState;
  month: Dayjs;
  service: number | undefined;
  offset: number;
  planId: number | undefined;
  clientPlanId: number | undefined;
  clientName: string | undefined;
  setClientName: (id: string | undefined) => void;
  setClientPlanId: (id: number | undefined) => void;
}

export const StateContext = createContext<PlanningContextType>({
  state: 'future',
  month: dayjs().add(1, 'month'),
  service: undefined,
  offset: 0,
  planId: undefined,
  clientPlanId: undefined,
  clientName: undefined,
  setClientName: (id: string | undefined) => {},
  setClientPlanId: (id: number | undefined) => {},
});

const overview = {
  label: 'Přehled plánování',
  value: 'overview',
};

const planning = {
  label: 'Plánování',
  value: 'planning',
};

const forPlanning = {
  label: 'K doplánování',
  value: 'for-planning',
};

const planned = {
  label: 'Naplánováno',
  value: 'planned',
};

const planAdjustment = {
  label: 'Úpravy plánu',
  value: 'plan-adjustment',
};

const plans = {
  label: 'Plány',
  value: 'plans',
};

const audit = {
  label: 'Kontrola výkazů',
  value: 'audit',
};

export const PlanningLayout = (): ReactElement => {
  const [month, setMonth] = useState<Dayjs>(
    dayjs().add(1, 'month').set('date', 1)
  );
  const [state, setState] = useState<PlanningState>('future');
  const [navigationOptions, setNavigationOptions] = useState<ComboboxItem[]>(
    []
  );
  const [service, setService] = useState<number | undefined>(1);
  const [size, setSize] = useState<number>(0);
  const [planId, setPlanId] = useState<number | undefined>(undefined);
  const [clientPlanId, setClientPlanId] = useState<number | undefined>(
    undefined
  );
  const [clientName, setClientName] = useState<string | undefined>(undefined);
  const divRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const obj = useMemo(
    () => ({
      state,
      month,
      service,
      offset: size,
      planId,
      clientPlanId,
      clientName,
      setClientName,
      setClientPlanId,
    }),
    [state, month, service, size, planId, clientPlanId, clientName]
  );

  useEffect(() => {
    switch (state) {
      case 'past':
        setNavigationOptions(planId ? [overview, plans, audit] : [overview]);
        break;
      case 'present':
        setNavigationOptions(
          planId ? [overview, planAdjustment, audit] : [overview]
        );
        break;
      case 'future':
        setNavigationOptions(
          planId
            ? [overview, planning, forPlanning, planned, audit]
            : [overview]
        );
        break;
    }
  }, [state, planId]);

  useEffect(() => {
    const fetchData = async () => {
      if (month.isBefore(dayjs().startOf('month'))) {
        setState('past');
      } else if (month.isAfter(dayjs().endOf('month'))) {
        setState('future');
      } else {
        setState('present');
      }

      try {
        const data = await getFetcher(
          `services/${service}/plans?month=${month.get('month') + 1}&year=${month.get('year')}`
        );
        setPlanId(data?.content?.[0]?.id);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [month, service]);

  useEffect(() => {
    if (divRef.current) {
      const divSize = divRef.current.getBoundingClientRect();
      setSize(divSize.height);
    }
  }, [divRef.current]);

  // Obnovení hodnot z localStorage při načtení stránky
  useEffect(() => {
    const savedState = localStorage.getItem('state');
    const savedMonth = localStorage.getItem('month');

    if (savedState) {
      setState(savedState as PlanningState);
      localStorage.removeItem('state');
    }

    if (savedMonth) {
      setMonth(dayjs(savedMonth));
      localStorage.removeItem('month');
    }
  }, []);

  const { data: availableServices } = useSWR<Services>('services', getFetcher);
  if (!availableServices) {
    return <div>Fetch error</div>;
  }

  const ServiceSelector = (
    <NativeSelect
      value={service}
      onChange={e => {
        setService(Number(e.currentTarget.value));
        navigate(`overview${location.search}`);
      }}
      data={availableServices?.map(service => ({
        value: service.id.toString(),
        label: service.name,
      }))}
    />
  );

  return (
    <StateContext.Provider value={obj}>
      <Stack ref={divRef} className="h-full p-8 overflow-auto">
        <MonthSelector
          value={month}
          setValue={setMonth}
          additionalActionOnClick={() => {
            navigate(`overview${location.search}`);
          }}
        />
        <SectionSelector
          removeSearchParams
          navigationOptions={navigationOptions}
          addons={[ServiceSelector]}
          sectionUrlIndex={1}
        />
        <Outlet />
      </Stack>
    </StateContext.Provider>
  );
};
